import React, { useMemo } from 'react';
import LiteBar from './LiteBar';
import { ActiveFunctions, DateRange, FunctionType, GateTimelineData, TimelineItem, TimelineViewport } from '@/types/item';
import { compareDates } from '@/utils/timelineCalculations';

interface LiteGateRowProps {
  gate: 'g1' | 'g2' | 'g3' | 'g4' | 'g5';
  item: TimelineItem;
  activeFunctions: ActiveFunctions;
  viewport: TimelineViewport;
  dateColumns: Date[];
  isMonthStart: (date: Date) => boolean;
  isWeekend: (date: Date) => boolean;
  isToday: (date: Date) => boolean;
}

const cellWidth = 32;

const LiteGateRow: React.FC<LiteGateRowProps> = ({
  gate,
  item,
  activeFunctions,
  viewport,
  dateColumns,
  isMonthStart,
  isWeekend,
  isToday,
}) => {
  const activeFunctionTypes = useMemo(
    () => Object.entries(activeFunctions).filter(([, v]) => v).map(([k]) => k as FunctionType),
    [activeFunctions]
  );

  const totalWidth = dateColumns.length * cellWidth;
  const laneHeight = 10;
  const lanes = Math.max(1, activeFunctionTypes.length);
  const rowHeight = 6 + lanes * laneHeight;

  // Aggregate gate range across active function types for a faint background bar
  const aggregatedRange: DateRange | undefined = useMemo(() => {
    let start: string | undefined;
    let end: string | undefined;
    activeFunctionTypes.forEach((ft) => {
      const d = item.timelineData[ft][gate];
      if (d) {
        // ✅ Timezone-aware comparison (prevents 1-day shift)
        if (!start || compareDates(d.start, start) < 0) start = d.start;
        if (!end || compareDates(d.end, end) > 0) end = d.end;
      }
    });
    return start && end ? { start, end } : undefined;
  }, [activeFunctionTypes, gate, item.timelineData]);

  return (
    <div className="flex">
      <div className="w-64 shrink-0 px-3 py-2 text-xs text-muted-foreground uppercase">{gate.toUpperCase()}</div>
      <div className="relative" style={{ width: totalWidth, height: rowHeight }}>
        {/* Background grid */}
        <div className="absolute inset-0">
          <div className="flex h-full">
            {dateColumns.map((date) => (
              <div
                key={`${gate}-${date.toISOString()}`}
                className={`w-8 h-full border-r ${isMonthStart(date) ? 'border-l-2 border-l-primary' : ''} ${isWeekend(date) ? 'bg-muted/20' : ''} ${isToday(date) ? 'bg-primary/10' : ''}`}
              />
            ))}
          </div>
        </div>

        {/* Aggregated faint bar */}
        {aggregatedRange && (
          <LiteBar
            dateRange={aggregatedRange}
            functionType="shop" // base color for faint background
            viewportStart={viewport.startDate}
            viewportEnd={viewport.endDate}
            height={12}
            top={2}
            opacity={0.25}
          />
        )}

        {/* Segments overlay */}
        <div className="absolute inset-0">
          {activeFunctionTypes.map((ft, laneIndex) => {
            const segments = item.timelineData[ft].segments?.[gate] || [];
            return segments.map((seg, segIndex) => (
              <LiteBar
                key={`${item.id}-${ft}-${gate}-${segIndex}`}
                dateRange={seg.dateRange}
                functionType={ft}
                viewportStart={viewport.startDate}
                viewportEnd={viewport.endDate}
                height={8}
                top={2 + laneIndex * laneHeight + segIndex * 2}
              />
            ));
          })}
        </div>
      </div>
    </div>
  );
};

export default React.memo(LiteGateRow);
